import { FC, Dispatch, SetStateAction, useState, useEffect } from 'react';
import { Notification, Person as PersonType } from '../../../types/global/types';
import './styles.scss';
import { Modal, Box, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { personSchema } from '../../../validationSchemas/personSchema';
import { generalActions } from '../../../backend/store/reducers/generalReducer';
import { actions as notificationActions } from '../../../backend/store/reducers/notificationsReducer';
import { personTriggerActions } from '../../../backend/store/reducers/personReducer';
import { addressTriggerActions } from '../../../backend/store/reducers/addressesReducer';
import { householdsTriggerActions } from '../../../backend/store/reducers/householdsReducer';
import { categoriesTriggerActions } from '../../../backend/store/reducers/categoriesReducer';
import { confirmPendingHouseholdNotification } from '../../../backend/store/actions/creators/confirmPendingHouseholdNotification';
import { dismissPendingHouseholdNotification } from '../../../backend/store/actions/creators/dismissPendingHouseholdNotification';
import { useTranslation } from '../../../hooks/useTranslation';
import { Button } from '../../ContactsPage/Button';
import { BlueButton } from '../BlueButton';
import { Person } from '../../VisitReportPage/Person';
import { Categories } from '../../CreateHouseholdPage/Categories';
import { PERSON } from '../../../constants/CreateHousehold';
import { FilterHeader20 } from '../FilterHeader20';

interface IPendingHouseholdNotificationProps {
  notification: Notification;
  setSelectedNotification: Dispatch<SetStateAction<Notification | null>>;
}

const mapAddress = (a: any = {}) => ({
  id: a.id,
  country: a.country ?? '',
  state: a.state ?? '',
  city: a.city ?? '',
  street_type: a.street_type ?? '',
  street_name: a.street_name ?? '',
  unit_number: a.unit_number ?? '',
  apartment_number: a.apartment_number ?? '',
  zip_code: a.zip_code ?? '',
  latitude: a.latitude ?? '',
  longitude: a.longitude ?? '',
  place_id: a.place_id ?? '',
  type: a.type ?? '',
  info_note: a.info_note ?? '',
  fullAddress: a.fullAddress ?? '',
  // The backend's AddressResource never sends a `formatted_address` key — only the
  // computed `fullAddress` accessor. Backfill it here so an untouched manual/text-only
  // address (whose only content signal IS this text) still has it once `fullAddress`
  // is stripped out again on submit below.
  formatted_address: a.formatted_address ?? a.fullAddress ?? '',
  set_as_household_address: !!a.set_as_household_address,
  is_manual: !!a.is_manual,
  is_text_only: !!a.is_text_only,
});

const mapPersons = (persons: any[] = []) =>
  persons.map((person) => ({
    id: person.id,
    first_name: person.first_name ?? '',
    last_name: person.last_name ?? '',
    email: person.email ?? '',
    salutation: person.salutation ?? '',
    gender: person.gender ?? '',
    is_child: person.is_child ? 1 : 0,
    is_jewish: person.is_jewish ? 1 : 0,
    info_note: person.info_note ?? '',
    isMain: !!person.isMain,
    set_as_main: person.isMain ? 1 : 0,
    phones: (person.phones ?? []).map((ph: any) => ({
      id: ph.id,
      number: ph.number ?? '',
      type: ph.type ?? '',
      area_code: ph.area_code ?? '',
      country: ph.country ?? '',
    })),
    addresses: (person.addresses ?? []).map(mapAddress),
  }));

export const PendingHouseholdNotification: FC<IPendingHouseholdNotificationProps> = ({
  notification,
  setSelectedNotification,
}) => {
  const dispatch = useDispatch();
  const { p } = useTranslation('notifications');

  useEffect(() => {
    dispatch(personTriggerActions.getSalutationsTriggerAC());
    dispatch(addressTriggerActions.getTypesTriggerAC());
    dispatch(householdsTriggerActions.getPeriodicityTriggerAC());
    dispatch(categoriesTriggerActions.getCategoriesTriggerAC());
  }, [dispatch]);

  const salutations = useSelector((state: any) => state.personsStore.salutations) || [];
  const addressesTypes = useSelector((state: any) => state.addressesStore.types) || [];
  const periodicityRaw = useSelector((state: any) => state.householdsStore.periodicity);
  const periodicityOptions = Array.isArray(periodicityRaw) ? periodicityRaw : (periodicityRaw?.data ?? []);
  const regularCategoriesOptions = useSelector((state: any) => state.categoriesStore.categories) || [];

  const pending = notification.pendingHousehold;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const formik = useFormik<{
    imported_name: string;
    periodicity: string;
    info_note: string;
    categories: any[];
    new_persons: PersonType[];
  }>({
    initialValues: {
      imported_name: pending?.imported_name ?? '',
      periodicity: pending?.periodicity ?? '',
      info_note: pending?.info_note ?? '',
      categories: (pending?.categories ?? []).map((c: any) => ({ id: c.id, name: c.name })),
      new_persons: mapPersons(pending?.persons) as any,
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      new_persons: Yup.array().of(personSchema),
    }),
    onSubmit: (values) => {
      // The backend validates id fields as strict integers (no nullable), so any
      // newly-added record carrying the constant's `id: ""` must drop the key.
      const dropEmptyId = ({ id, ...rest }: any) =>
        typeof id === 'number' && id > 0 ? { id, ...rest } : rest;

      const persons = (values.new_persons || []).map((person: any) => {
        const addresses = (person.addresses || [])
          .filter((a: any) => a.is_manual || a.is_text_only || (a.country && a.latitude !== '' && a.latitude != null))
          .map(({ fullAddress, person_id, created_at, updated_at, new_address, ...rest }: any) =>
            dropEmptyId(rest)
          );
        const phones = (person.phones || [])
          .filter((ph: any) => ph.number && ph.number.trim() !== '')
          .map((ph: any) => dropEmptyId(ph));
        return dropEmptyId({ ...person, addresses, phones });
      });

      const categories = (values.categories || [])
        .filter((c: any) => c && typeof c.id === 'number')
        .map((c: any) => c.id);

      dispatch(
        confirmPendingHouseholdNotification({
          id: notification.id,
          data: {
            imported_name: values.imported_name,
            periodicity: values.periodicity,
            info_note: values.info_note,
            categories,
            persons,
          },
          onSuccess: () => {
            dispatch(generalActions.successAC({ message: p('pending_household_confirmed'), type: 'success' }));
            dispatch(notificationActions.removeNotificationFromList(notification.id));
            setIsModalOpen(false);
            setSelectedNotification(null);
          },
          onError: () => {
            dispatch(generalActions.errorAC({ message: p('pending_household_error'), type: 'error' }));
          },
        })
      );
    },
  });

  const handleDismiss = () => {
    dispatch(
      dismissPendingHouseholdNotification({
        id: notification.id,
        onSuccess: () => {
          dispatch(generalActions.successAC({ message: p('pending_household_dismissed'), type: 'success' }));
          dispatch(notificationActions.removeNotificationFromList(notification.id));
          setSelectedNotification(null);
        },
        onError: () => {
          dispatch(generalActions.errorAC({ message: p('pending_household_error'), type: 'error' }));
        },
      })
    );
  };

  const addPerson = () => {
    formik.setFieldValue('new_persons', [...(formik.values.new_persons || []), PERSON]);
  };

  return (
    <div className="notification">
      <div className="notification__header">
        <div className="notification__title">
          <h3>
            {notification.title.startOfTitle}
            {notification.title.householdName}
            {notification.title.endOfTitle}
          </h3>
          <div className="notification__time">{notification.created_at_formatted}</div>
        </div>
      </div>

      <div className="notification__info">{p('pending_household_info')}</div>

      <div className="notification__info-button">
        <BlueButton title={p('pending_household_view_edit')} onClick={() => setIsModalOpen(true)} />
      </div>

      <div className="notification__buttons">
        <Button title={p('pending_household_confirm')} onClick={() => formik.handleSubmit()} />
        <Button title={p('pending_household_dismiss')} white onClick={handleDismiss} />
      </div>

      <Modal
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
        open={isModalOpen}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            width: '90%',
            maxWidth: 720,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FilterHeader20 title={p('pending_household_modal_title')} onClose={() => setIsModalOpen(false)} />

          <Box sx={{ overflowY: 'auto', p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label={p('household_name')}
                value={formik.values.imported_name}
                onChange={(e) => formik.setFieldValue('imported_name', e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <TextField
                size="small"
                label={p('info_note')}
                value={formik.values.info_note}
                onChange={(e) => formik.setFieldValue('info_note', e.target.value)}
                multiline
                sx={{ flex: 1, minWidth: 240 }}
              />
            </Box>

            <Categories
              formik={formik as any}
              periodicityOptions={periodicityOptions}
              regularCategorisOptions={regularCategoriesOptions}
            />

            {(formik.values.new_persons || []).map((_: any, index: number) => (
              <Person
                key={index}
                formik={formik as any}
                personIndex={index}
                salutations={salutations}
                addressesTypes={addressesTypes}
              />
            ))}

            <Box sx={{ mt: 1 }}>
              <BlueButton title={p('pending_household_add_contact')} onClick={addPerson} />
            </Box>
          </Box>

          <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
            <Button title={p('pending_household_confirm')} onClick={() => formik.handleSubmit()} />
          </Box>
        </Box>
      </Modal>
    </div>
  );
};
